import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  ArrowRight,
  Plus,
  Trash2,
  Loader2,
  Shield,
  Pencil,
  UserPlus,
  Users,
  Atom,
  ChevronDown,
  ChevronRight,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle,
  Lock,
  ShieldAlert,
  HardDrive,
  Wifi,
  WifiOff,
  LockKeyhole,
  LockOpen,
  Wrench,
  Settings as SettingsIcon,
} from "lucide-react";
import { toast } from "sonner";
import KeyRotationBanner from "../components/KeyRotationBanner";
import { format } from "date-fns";
import { it as itLocale, enUS as enLocale } from "date-fns/locale";
import { ColorPicker } from "../components/ColorPicker";
import { useLanguage } from "../context/LanguageContext";
import { PasswordInput } from "../components/PasswordInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Both encryption keys are hashed rather than stretched, so a short one is
// brute-forceable offline by anyone holding a dump or the bucket. The server
// refuses anything shorter for a new key.
const MIN_KEY_LENGTH = 32;

// Built here rather than written into the markup. An escape sequence works in a
// JavaScript string but is printed character for character inside JSX text, so
// putting it there directly would show the sequence instead of the symbol.
const HALF_LIFE_SYMBOL = "t\u00bd";

export default function Settings() {
  const { user: currentUser } = useAuth();
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // User edit form
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userConfirmPassword, setUserConfirmPassword] = useState("");
  const [userColor, setUserColor] = useState("#3730A3");
  const [savingUser, setSavingUser] = useState(false);

  // New user creation form
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserConfirmPassword, setNewUserConfirmPassword] = useState("");
  const [newUserDisplayName, setNewUserDisplayName] = useState("");
  const [newUserRole, setNewUserRole] = useState("user");
  const [newUserColor, setNewUserColor] = useState("#3730A3");
  const [creatingUser, setCreatingUser] = useState(false);

  // Isotope management
  const [elements, setElements] = useState([]);
  const [expandedElements, setExpandedElements] = useState({});
  const [elementDialogOpen, setElementDialogOpen] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [elementName, setElementName] = useState("");
  const [savingElement, setSavingElement] = useState(false);
  const [isotopeDialogOpen, setIsotopeDialogOpen] = useState(false);
  const [editingIsotope, setEditingIsotope] = useState(null);
  const [isotopeElementId, setIsotopeElementId] = useState("");
  const [isotopeName, setIsotopeName] = useState("");
  const [isotopeHalfLifeValue, setIsotopeHalfLifeValue] = useState("");
  const [isotopeHalfLifeUnit, setIsotopeHalfLifeUnit] = useState("minutes");
  const [savingIsotope, setSavingIsotope] = useState(false);

  const HALF_LIFE_UNITS = ["seconds", "minutes", "hours", "days", "years"];

  // SMTP settings
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpSenderEmail, setSmtpSenderEmail] = useState("");
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [togglingTwoFa, setTogglingTwoFa] = useState(false);

  // Section navigation
  const [activeSection, setActiveSection] = useState("users");

  // Maintenance mode
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceReason, setMaintenanceReason] = useState("");
  const [togglingMaintenance, setTogglingMaintenance] = useState(false);

  // The two encryption keys. Neither is rotated from this page: both
  // procedures have steps, warnings and a state that outlives a visit, so what
  // belongs here is the state and the way in.
  const [dbKeyStatus, setDbKeyStatus] = useState(null);
  const [fileKeyStatus, setFileKeyStatus] = useState(null);

  // R2 Configuration
  const [r2AccessKeyId, setR2AccessKeyId] = useState("");
  const [r2SecretAccessKey, setR2SecretAccessKey] = useState("");
  const [r2EndpointUrl, setR2EndpointUrl] = useState("");
  const [r2BucketName, setR2BucketName] = useState("");
  const [r2Configured, setR2Configured] = useState(false);
  const [savingR2, setSavingR2] = useState(false);
  const [testingR2, setTestingR2] = useState(false);
  const [r2TestResult, setR2TestResult] = useState(null);

  // Backup R2 Configuration
  const [bkR2AccessKeyId, setBkR2AccessKeyId] = useState("");
  const [bkR2SecretAccessKey, setBkR2SecretAccessKey] = useState("");
  const [bkR2EndpointUrl, setBkR2EndpointUrl] = useState("");
  const [bkR2BucketName, setBkR2BucketName] = useState("");
  const [bkR2Configured, setBkR2Configured] = useState(false);
  const [savingBkR2, setSavingBkR2] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, elementsRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/elements`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setElements(elementsRes.data);

      // Fetch SMTP settings separately (admin only, may fail for regular users)
      try {
        const smtpRes = await axios.get(`${API}/smtp-settings`, { withCredentials: true });
        const s = smtpRes.data;
        setSmtpHost(s.host || "");
        setSmtpPort(String(s.port || 587));
        setSmtpUsername(s.username || "");
        setSmtpPassword(s.password || "");
        setSmtpSenderEmail(s.sender_email || "");
        setSmtpConfigured(s.configured || false);
      } catch {
        // Non-admin user or not configured
      }

      // Fetch 2FA toggle state
      try {
        const tfaRes = await axios.get(`${API}/settings/2fa-toggle`, { withCredentials: true });
        setTwoFaEnabled(tfaRes.data.enabled || false);
      } catch {
        // Non-admin or not set
      }

      // Maintenance state
      try {
        const mRes = await axios.get(`${API}/maintenance`, { withCredentials: true });
        setMaintenance(mRes.data);
      } catch {
        // Unreachable: shown as off
      }

      // Database key: whether a rotation is waiting on Railway
      try {
        const dbRes = await axios.get(`${API}/security/key-status`, { withCredentials: true });
        setDbKeyStatus(dbRes.data);
      } catch {
        // Non-admin
      }

      // File key: length, and whether a rotation is under way
      try {
        const fileKeyRes = await axios.get(`${API}/file-key/status`, { withCredentials: true });
        setFileKeyStatus(fileKeyRes.data);
      } catch {
        // Non-admin, or storage not configured yet
      }

      // Fetch R2 config
      try {
        const r2Res = await axios.get(`${API}/settings/r2`, { withCredentials: true });
        setR2AccessKeyId(r2Res.data.access_key_id || "");
        setR2SecretAccessKey(r2Res.data.secret_access_key || "");
        setR2EndpointUrl(r2Res.data.endpoint_url || "");
        setR2BucketName(r2Res.data.bucket_name || "");
        setR2Configured(r2Res.data.configured || false);
      } catch {
        // Non-admin or not configured
      }

      // Fetch Backup R2 config
      try {
        const bkRes = await axios.get(`${API}/settings/backup-r2`, { withCredentials: true });
        setBkR2AccessKeyId(bkRes.data.access_key_id || "");
        setBkR2SecretAccessKey(bkRes.data.secret_access_key || "");
        setBkR2EndpointUrl(bkRes.data.endpoint_url || "");
        setBkR2BucketName(bkRes.data.bucket_name || "");
        setBkR2Configured(bkRes.data.configured || false);
      } catch {
        // Non-admin or not configured
      }

    } catch (error) {
      toast.error(t("toast.failedLoad"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleMaintenance = async () => {
    const turningOn = !maintenance?.active;
    setTogglingMaintenance(true);
    try {
      const { data } = await axios.post(
        `${API}/maintenance`,
        { active: turningOn, reason: turningOn ? maintenanceReason.trim() : "" },
        { withCredentials: true }
      );
      setMaintenance(data);
      if (turningOn) setMaintenanceReason("");
      toast.success(turningOn ? "Modalità manutenzione attivata" : "Modalità manutenzione disattivata");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.actionFailed"));
    } finally {
      setTogglingMaintenance(false);
    }
  };

  // User handlers
  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    if (userPassword) {
      if (userPassword !== userConfirmPassword) {
        toast.error(t("pwd.mismatch"));
        return;
      }
    }

    setSavingUser(true);
    try {
      const updateData = {};
      if (userDisplayName && userDisplayName !== editingUser.display_name) updateData.display_name = userDisplayName;
      if (userRole && userRole !== editingUser.role) updateData.role = userRole;
      if (userColor && userColor !== editingUser.color) updateData.color = userColor;
      if (userPassword) updateData.password = userPassword;

      if (Object.keys(updateData).length === 0) {
        toast.info(t("toast.noChanges"));
        setUserDialogOpen(false);
        return;
      }

      const { data } = await axios.patch(
        `${API}/users/${editingUser._id}`,
        updateData,
        { withCredentials: true }
      );
      setUsers(users.map((u) => (u._id === data._id ? data : u)));
      toast.success(t("toast.userUpdated"));
      setUserDialogOpen(false);
      setEditingUser(null);
      setUserPassword("");
      setUserConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("users.updateFailed"));
    } finally {
      setSavingUser(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (newUserPassword !== newUserConfirmPassword) {
      toast.error(t("pwd.mismatch"));
      return;
    }

    setCreatingUser(true);
    try {
      const { data } = await axios.post(
        `${API}/users`,
        {
          email: newUserEmail,
          password: newUserPassword,
          display_name: newUserDisplayName,
          role: newUserRole,
          color: newUserColor,
        },
        { withCredentials: true }
      );
      setUsers([...users, data]);
      toast.success(t("toast.userCreated"));
      setCreateUserDialogOpen(false);
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserConfirmPassword("");
      setNewUserDisplayName("");
      setNewUserRole("user");
      setNewUserColor("#3730A3");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("users.createFailed"));
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      setUsers(users.filter((u) => u._id !== userId));
      toast.success(t("toast.userDeleted"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("users.deleteFailed"));
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/unlock`, {}, { withCredentials: true });
      setUsers(users.map((u) => u._id === userId ? { ...u, is_locked: false, failed_login_attempts: 0 } : u));
      toast.success(t("users.unlockSuccess"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("users.unlockError"));
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserDisplayName(user.display_name || "");
    setUserRole(user.role);
    setUserColor(user.color || "#3730A3");
    setUserPassword("");
    setUserConfirmPassword("");
    setUserDialogOpen(true);
  };

  // Dates follow the language chosen in Core, not the one the operating system
  // happens to be in. Without a locale date-fns writes English month names to
  // an Italian page.
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d MMM yyyy", {
        locale: language === "it" ? itLocale : enLocale,
      });
    } catch {
      return dateString;
    }
  };

  // ========== Isotope Management ==========
  const toggleElement = (id) => {
    setExpandedElements((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openNewElement = () => {
    setEditingElement(null);
    setElementName("");
    setElementDialogOpen(true);
  };

  const openEditElement = (el) => {
    setEditingElement(el);
    setElementName(el.name);
    setElementDialogOpen(true);
  };

  const handleSaveElement = async (e) => {
    e.preventDefault();
    setSavingElement(true);
    try {
      if (editingElement) {
        const { data } = await axios.patch(`${API}/elements/${editingElement.id}`, { name: elementName }, { withCredentials: true });
        setElements(elements.map((el) => (el.id === data.id ? data : el)));
      } else {
        const { data } = await axios.post(`${API}/elements`, { name: elementName }, { withCredentials: true });
        setElements([...elements, data]);
        setExpandedElements((prev) => ({ ...prev, [data.id]: true }));
      }
      setElementDialogOpen(false);
      toast.success(t("common.save"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.actionFailed"));
    } finally {
      setSavingElement(false);
    }
  };

  const handleDeleteElement = async (elementId) => {
    try {
      await axios.delete(`${API}/elements/${elementId}`, { withCredentials: true });
      setElements(elements.filter((el) => el.id !== elementId));
      toast.success(t("common.delete"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.actionFailed"));
    }
  };

  const openNewIsotope = (elementId) => {
    setEditingIsotope(null);
    setIsotopeElementId(elementId);
    setIsotopeName("");
    setIsotopeHalfLifeValue("");
    setIsotopeHalfLifeUnit("minutes");
    setIsotopeDialogOpen(true);
  };

  const openEditIsotope = (isotope) => {
    setEditingIsotope(isotope);
    setIsotopeElementId(isotope.element_id);
    setIsotopeName(isotope.name);
    setIsotopeHalfLifeValue(String(isotope.half_life_value));
    setIsotopeHalfLifeUnit(isotope.half_life_unit);
    setIsotopeDialogOpen(true);
  };

  const handleSaveIsotope = async (e) => {
    e.preventDefault();
    setSavingIsotope(true);
    try {
      if (editingIsotope) {
        const { data } = await axios.patch(`${API}/isotopes/${editingIsotope.id}`, {
          name: isotopeName,
          half_life_value: parseFloat(isotopeHalfLifeValue),
          half_life_unit: isotopeHalfLifeUnit,
        }, { withCredentials: true });
        setElements(elements.map((el) => ({
          ...el,
          isotopes: el.isotopes.map((iso) => (iso.id === data.id ? data : iso)),
        })));
      } else {
        const { data } = await axios.post(`${API}/elements/${isotopeElementId}/isotopes`, {
          name: isotopeName,
          half_life_value: parseFloat(isotopeHalfLifeValue),
          half_life_unit: isotopeHalfLifeUnit,
        }, { withCredentials: true });
        setElements(elements.map((el) =>
          el.id === isotopeElementId ? { ...el, isotopes: [...el.isotopes, data] } : el
        ));
      }
      setIsotopeDialogOpen(false);
      toast.success(t("common.save"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.actionFailed"));
    } finally {
      setSavingIsotope(false);
    }
  };

  const handleDeleteIsotope = async (isotopeId, elementId) => {
    try {
      await axios.delete(`${API}/isotopes/${isotopeId}`, { withCredentials: true });
      setElements(elements.map((el) =>
        el.id === elementId ? { ...el, isotopes: el.isotopes.filter((iso) => iso.id !== isotopeId) } : el
      ));
      toast.success(t("common.delete"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.actionFailed"));
    }
  };

  // ========== SMTP Handlers ==========
  const handleToggleTwoFa = async () => {
    setTogglingTwoFa(true);
    try {
      const res = await axios.post(`${API}/settings/2fa-toggle`, { enabled: !twoFaEnabled }, { withCredentials: true });
      setTwoFaEnabled(res.data.enabled);
      toast.success(res.data.enabled ? t("smtp.2faEnabled") : t("smtp.2faDisabled"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("common.actionFailed"));
    } finally {
      setTogglingTwoFa(false);
    }
  };

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setSavingSmtp(true);
    try {
      await axios.post(`${API}/smtp-settings`, {
        host: smtpHost,
        port: parseInt(smtpPort, 10),
        username: smtpUsername,
        password: smtpPassword,
        sender_email: smtpSenderEmail,
      }, { withCredentials: true });
      setSmtpConfigured(true);
      toast.success(t("smtp.saved"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("smtp.saveFailed"));
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) return;
    setSendingTest(true);
    try {
      await axios.post(`${API}/smtp-settings/test`, { to_email: testEmail }, { withCredentials: true });
      toast.success(t("smtp.testSuccess"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("smtp.testFailed"));
    } finally {
      setSendingTest(false);
    }
  };

  // ========== R2 Configuration Handlers ==========
  const handleSaveR2 = async (e) => {
    e.preventDefault();
    setSavingR2(true);
    setR2TestResult(null);
    try {
      await axios.post(`${API}/settings/r2`, {
        access_key_id: r2AccessKeyId,
        secret_access_key: r2SecretAccessKey,
        endpoint_url: r2EndpointUrl,
        bucket_name: r2BucketName,
      }, { withCredentials: true });
      setR2Configured(true);
      toast.success(t("r2.saved"));
      // Re-fetch to get masked values. The server treats a submitted value that
      // equals the mask of the stored one as unchanged, so reading them back
      // here does not put the next save at risk.
      try {
        const r2Res = await axios.get(`${API}/settings/r2`, { withCredentials: true });
        setR2AccessKeyId(r2Res.data.access_key_id || "");
        setR2SecretAccessKey(r2Res.data.secret_access_key || "");
        setR2EndpointUrl(r2Res.data.endpoint_url || "");
        setR2BucketName(r2Res.data.bucket_name || "");
      } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || t("r2.saveFailed"));
    } finally {
      setSavingR2(false);
    }
  };

  const handleTestR2 = async () => {
    setTestingR2(true);
    setR2TestResult(null);
    try {
      const res = await axios.post(`${API}/settings/r2/test`, {}, { withCredentials: true });
      setR2TestResult(res.data);
    } catch (error) {
      setR2TestResult({ connected: false, error: error.response?.data?.detail || t("r2.testFailed") });
    } finally {
      setTestingR2(false);
    }
  };

  const handleSaveBkR2 = async (e) => {
    e.preventDefault();
    setSavingBkR2(true);
    try {
      await axios.post(`${API}/settings/backup-r2`, {
        access_key_id: bkR2AccessKeyId,
        secret_access_key: bkR2SecretAccessKey,
        endpoint_url: bkR2EndpointUrl,
        bucket_name: bkR2BucketName,
      }, { withCredentials: true });
      setBkR2Configured(true);
      toast.success(t("backupR2.saved"));
      try {
        const bkRes = await axios.get(`${API}/settings/backup-r2`, { withCredentials: true });
        setBkR2AccessKeyId(bkRes.data.access_key_id || "");
        setBkR2SecretAccessKey(bkRes.data.secret_access_key || "");
        setBkR2EndpointUrl(bkRes.data.endpoint_url || "");
        setBkR2BucketName(bkRes.data.bucket_name || "");
      } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || t("backupR2.saveFailed"));
    } finally {
      setSavingBkR2(false);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0 },
  };

  // Projects no longer appear here. Their colour is chosen per project by
  // whoever creates it, so there is no admin-managed list left to maintain.
  const NAV_SECTIONS = [
    { id: "users", icon: Users, labelKey: "settings.users", descKey: "settings.usersDesc" },
    { id: "email", icon: Mail, labelKey: "settings.email", descKey: "settings.emailDesc" },
    { id: "decay", icon: Atom, labelKey: "settings.decayCalculator", descKey: "settings.decayDesc" },
    { id: "security", icon: Lock, labelKey: "settings.security", descKey: "settings.securityDesc" },
    { id: "storage", icon: HardDrive, labelKey: "r2.title", descKey: "r2.description" },
  ];

  const PANEL_TONES = {
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    busy: "bg-sky-50 text-sky-700 border-sky-200",
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };

  const maintenanceOn = maintenance?.active === true;

  const dbKeyPanel = dbKeyStatus?.db_key?.needs_railway_update
    ? {
        tone: "warn",
        badge: "Azione richiesta",
        message: "La chiave è stata ruotata ma la variabile su Railway non è ancora aggiornata. Al prossimo riavvio Core tornerebbe alla chiave precedente.",
        cta: "Completa la procedura",
      }
    : {
        tone: "good",
        badge: t("security.active"),
        message: "Nomi, email, progetti, aggiornamenti, trasferte e clienti sono cifrati nel database.",
        cta: "Ruota la chiave",
      };

  const fileKeyPanel = (() => {
    if (!fileKeyStatus) return null;
    if (fileKeyStatus.step === "copying") {
      return {
        tone: "busy",
        badge: "Rotazione in corso",
        message: `${fileKeyStatus.copied_files} di ${fileKeyStatus.total_files} file copiati con la chiave nuova. I file in uso non sono stati modificati.`,
        cta: "Riprendi la rotazione",
      };
    }
    if (fileKeyStatus.step === "ready_to_switch") {
      return {
        tone: "busy",
        badge: "Da completare",
        message: "Tutti i file sono stati copiati. Restano la verifica e l'attivazione.",
        cta: "Verifica e attiva",
      };
    }
    if (fileKeyStatus.step === "switched") {
      return {
        tone: "good",
        badge: "Ruotata",
        message: "Core legge la copia nuova. La copia precedente esiste ancora.",
        cta: "Apri la procedura",
      };
    }
    if (fileKeyStatus.key_is_weak) {
      return {
        tone: "warn",
        badge: "Chiave debole",
        message: `La chiave è lunga ${fileKeyStatus.key_length} caratteri invece di ${MIN_KEY_LENGTH}.`,
        cta: "Ruota la chiave",
      };
    }
    return {
      tone: "good",
      badge: t("security.active"),
      message: `Chiave di ${fileKeyStatus.key_length} caratteri, ${fileKeyStatus.total_files} file cifrati.`,
      cta: "Ruota la chiave",
    };
  })();

  return (
    <div className="min-h-screen bg-slate-50">

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Back link */}
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("settings.backToHome")}
          </Link>

          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-slate-600 flex items-center justify-center shrink-0">
              <SettingsIcon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("settings.title")}</h1>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-4 md:gap-8">
              {/* Sidebar Navigation */}
              <nav className="w-56 shrink-0 hidden md:block">
                <div className="sticky top-8 space-y-1">
                  {NAV_SECTIONS.map((sec) => {
                    const Icon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        data-testid={`nav-${sec.id}`}
                        onClick={() => setActiveSection(sec.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-3 ${
                          isActive
                            ? "bg-indigo-50 text-indigo-700 font-medium"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-indigo-600" : "text-slate-400"}`} />
                        <div className="min-w-0">
                          <span className="text-sm block">{t(sec.labelKey)}</span>
                          <span className={`text-xs block ${isActive ? "text-indigo-500" : "text-slate-400"}`}>{t(sec.descKey)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </nav>

              {/* Mobile section selector */}
              <div className="md:hidden w-full mb-4">
                <Select value={activeSection} onValueChange={setActiveSection}>
                  <SelectTrigger data-testid="mobile-section-select" className="border-slate-200 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NAV_SECTIONS.map((sec) => (
                      <SelectItem key={sec.id} value={sec.id}>{t(sec.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Content Area */}
              <div className="flex-1 min-w-0">

                {/* ====== USERS SECTION ====== */}
                {activeSection === "users" && (
                  <motion.div key="users" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-slate-900">{t("settings.users")}</h2>
                        <p className="text-sm text-slate-500">{t("settings.usersDesc")}</p>
                      </div>
                      <Button
                        onClick={() => setCreateUserDialogOpen(true)}
                        data-testid="add-user-btn"
                        className="bg-indigo-700 hover:bg-indigo-600 text-white shrink-0"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        {t("settings.addUser")}
                      </Button>
                    </div>

                    {/* overflow-x-auto only: pairing it with overflow-hidden left the
                        table clipped on a narrow screen with no way to scroll to
                        the Role and Actions columns. */}
                    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
                      <table className="w-full min-w-[600px]" data-testid="users-table">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">{t("users.user")}</th>
                            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">{t("users.role")}</th>
                            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">{t("users.joined")}</th>
                            <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">{t("users.actions")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {users.map((user) => (
                            <motion.tr
                              key={user._id}
                              variants={itemVariants}
                              data-testid={`user-row-${user._id}`}
                              className="hover:bg-slate-50 transition-colors"
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-9 h-9 rounded-full flex items-center justify-center text-white font-medium shrink-0"
                                    style={{ backgroundColor: user.color || '#3730A3' }}
                                  >
                                    {user.display_name?.charAt(0)?.toUpperCase() || 'U'}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-slate-900 break-words">{user.display_name}</p>
                                    <p className="text-sm text-slate-500 break-all">{user.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  {user.role === "admin" ? (
                                    <div className="flex items-center gap-1.5">
                                      <Shield className="w-4 h-4 text-indigo-600" />
                                      <span className="text-sm font-medium text-indigo-700">{t("users.admin")}</span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-slate-600">{t("users.userRole")}</span>
                                  )}
                                  {user.is_locked && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200" data-testid={`locked-badge-${user._id}`}>
                                      <LockKeyhole className="w-3 h-3" />
                                      {t("users.locked")}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">{formatDate(user.created_at)}</td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {user.is_locked && (
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button variant="ghost" size="icon" data-testid={`unlock-user-${user._id}`} className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50" title={t("users.unlock")}>
                                          <LockOpen className="w-4 h-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>{t("users.unlockConfirmTitle")}</AlertDialogTitle>
                                          <AlertDialogDescription>{t("users.unlockConfirmDesc", { name: user.display_name })}</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel className="border-slate-200">{t("common.cancel")}</AlertDialogCancel>
                                          <AlertDialogAction onClick={() => handleUnlockUser(user._id)} data-testid="confirm-unlock-user" className="bg-indigo-700 hover:bg-indigo-600">{t("users.unlock")}</AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  )}
                                  <Button variant="ghost" size="icon" data-testid={`edit-user-${user._id}`} onClick={() => openEditUser(user)} className="h-8 w-8 text-slate-500 hover:text-indigo-700">
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  {user._id !== currentUser?._id && (
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button variant="ghost" size="icon" data-testid={`delete-user-${user._id}`} className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50">
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>{t("users.delete.title")}</AlertDialogTitle>
                                          <AlertDialogDescription>{t("users.delete.desc", { name: user.display_name })}</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel className="border-slate-200">{t("users.delete.cancel")}</AlertDialogCancel>
                                          <AlertDialogAction onClick={() => handleDeleteUser(user._id)} data-testid="confirm-delete-user" className="bg-red-600 hover:bg-red-700">{t("users.delete.confirm")}</AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  )}
                                </div>
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                )}

                {/* ====== EMAIL / SMTP SECTION ====== */}
                {activeSection === "email" && (
                  <motion.div key="email" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                    <div className="space-y-6">
                      {/* Header with status badge */}
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="text-lg font-semibold text-slate-900">{t("smtp.title")}</h2>
                          <p className="text-sm text-slate-500 mt-1">{t("smtp.description")}</p>
                        </div>
                        <span
                          data-testid="smtp-status-badge"
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                            smtpConfigured
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {smtpConfigured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                          {smtpConfigured ? t("smtp.configured") : t("smtp.notConfiguredBadge")}
                        </span>
                      </div>

                      {/* 2FA Global Toggle */}
                      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="2fa-toggle-section">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-start gap-3 min-w-0">
                            <Shield className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-slate-800">{t("smtp.2faToggleTitle")}</h3>
                              <p className="text-xs text-slate-500 mt-0.5">{t("smtp.2faToggleDesc")}</p>
                            </div>
                          </div>
                          <button
                            onClick={handleToggleTwoFa}
                            disabled={togglingTwoFa}
                            data-testid="2fa-toggle-btn"
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${twoFaEnabled ? "bg-indigo-600" : "bg-slate-300"} ${togglingTwoFa ? "opacity-50" : ""}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${twoFaEnabled ? "translate-x-6" : "translate-x-1"}`} />
                          </button>
                        </div>
                        <div className={`mt-2 ml-8 text-xs font-medium ${twoFaEnabled ? "text-emerald-600" : "text-slate-400"}`}>
                          {twoFaEnabled ? t("smtp.2faStatusOn") : t("smtp.2faStatusOff")}
                        </div>
                      </div>

                      {/* SMTP Settings Form */}
                      <form onSubmit={handleSaveSmtp} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                        <div className="grid gap-5 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="smtpHost">{t("smtp.host")}</Label>
                            <Input id="smtpHost" data-testid="smtp-host-input" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder={t("smtp.hostPlaceholder")} required className="border-slate-200" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="smtpPort">{t("smtp.port")}</Label>
                            <Input id="smtpPort" data-testid="smtp-port-input" type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} placeholder="587" required className="border-slate-200" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="smtpUsername">{t("smtp.username")}</Label>
                            <Input id="smtpUsername" data-testid="smtp-username-input" value={smtpUsername} onChange={(e) => setSmtpUsername(e.target.value)} placeholder={t("smtp.usernamePlaceholder")} required className="border-slate-200" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="smtpPassword">{t("smtp.password")}</Label>
                            <Input id="smtpPassword" data-testid="smtp-password-input" type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} placeholder={t("smtp.passwordPlaceholder")} required className="border-slate-200" />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="smtpSenderEmail">{t("smtp.senderEmail")}</Label>
                            <Input id="smtpSenderEmail" data-testid="smtp-sender-input" type="email" value={smtpSenderEmail} onChange={(e) => setSmtpSenderEmail(e.target.value)} placeholder={t("smtp.senderPlaceholder")} required className="border-slate-200" />
                          </div>
                        </div>
                        <div className="flex justify-end mt-6">
                          <Button type="submit" data-testid="smtp-save-btn" disabled={savingSmtp} className="bg-indigo-700 hover:bg-indigo-600 text-white w-full sm:w-auto">
                            {savingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                            {savingSmtp ? t("smtp.saving") : t("smtp.save")}
                          </Button>
                        </div>
                      </form>

                      {/* Test Email Section */}
                      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                        <h4 className="text-sm font-semibold text-slate-900 mb-4">{t("smtp.testTitle")}</h4>
                        {!smtpConfigured ? (
                          <p className="text-sm text-slate-500">{t("smtp.notConfigured")}</p>
                        ) : (
                          /* Stacked below sm: the send button never shrinks below its
                             label, so side by side it squeezed the address field to
                             nothing on a phone. */
                          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                            <div className="flex-1 min-w-0 space-y-2">
                              <Label htmlFor="testEmail">{t("smtp.testEmail")}</Label>
                              <Input id="testEmail" data-testid="smtp-test-email-input" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder={t("smtp.testEmailPlaceholder")} className="border-slate-200" />
                            </div>
                            <Button type="button" onClick={handleSendTestEmail} data-testid="smtp-test-send-btn" disabled={sendingTest || !testEmail.trim()} className="bg-emerald-600 hover:bg-emerald-500 text-white w-full sm:w-auto">
                              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                              {sendingTest ? t("smtp.sending") : t("smtp.sendTest")}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ====== DECAY CALCULATOR (ISOTOPES) SECTION ====== */}
                {activeSection === "decay" && (
                  <motion.div key="decay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-slate-900">{t("settings.decayCalculator")}</h2>
                        <p className="text-sm text-slate-500">{t("settings.decayDesc")}</p>
                      </div>
                      <Button onClick={openNewElement} data-testid="add-element-btn" className="bg-indigo-700 hover:bg-indigo-600 text-white shrink-0">
                        <Plus className="w-4 h-4 mr-2" />
                        {t("isotopes.addElement")}
                      </Button>
                    </div>

                    {elements.length === 0 ? (
                      <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
                        <Atom className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                        <p className="text-slate-500">{t("isotopes.noElements")}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {elements.map((el) => (
                          <div key={el.id} data-testid={`element-${el.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                            <div
                              className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                              onClick={() => toggleElement(el.id)}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                {expandedElements[el.id] ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                                <Atom className="w-4 h-4 text-emerald-600 shrink-0" />
                                <span className="font-medium text-slate-800 break-words">{el.name}</span>
                                <span className="text-xs text-slate-400 shrink-0">({el.isotopes?.length || 0})</span>
                              </div>
                              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" onClick={() => openEditElement(el)} data-testid={`edit-element-${el.id}`} className="h-7 w-7 text-slate-400 hover:text-indigo-600">
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => openNewIsotope(el.id)} data-testid={`add-isotope-${el.id}`} className="h-7 w-7 text-slate-400 hover:text-emerald-600">
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" data-testid={`delete-element-${el.id}`} className="h-7 w-7 text-slate-400 hover:text-red-600">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t("isotopes.deleteElement")}</AlertDialogTitle>
                                      <AlertDialogDescription>{t("isotopes.deleteElementDesc", { name: el.name })}</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="border-slate-200">{t("common.cancel")}</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteElement(el.id)} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                            {expandedElements[el.id] && (
                              <div className="border-t border-slate-100 bg-slate-50">
                                {el.isotopes?.length > 0 ? (
                                  el.isotopes.map((iso) => (
                                    <div key={iso.id} data-testid={`isotope-${iso.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 pl-12 border-b border-slate-100 last:border-b-0">
                                      <div className="min-w-0">
                                        <span className="text-sm font-medium text-slate-700 break-words">{iso.name}</span>
                                        <span className="text-xs text-slate-400 ml-3">{HALF_LIFE_SYMBOL} = {iso.half_life_value} {t(`isotopes.${iso.half_life_unit}`)}</span>
                                      </div>
                                      <div className="flex items-center gap-1 shrink-0">
                                        <Button variant="ghost" size="icon" onClick={() => openEditIsotope(iso)} data-testid={`edit-isotope-${iso.id}`} className="h-7 w-7 text-slate-400 hover:text-indigo-600">
                                          <Pencil className="w-3 h-3" />
                                        </Button>
                                        <AlertDialog>
                                          <AlertDialogTrigger asChild>
                                            <Button variant="ghost" size="icon" data-testid={`delete-isotope-${iso.id}`} className="h-7 w-7 text-slate-400 hover:text-red-600">
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          </AlertDialogTrigger>
                                          <AlertDialogContent>
                                            <AlertDialogHeader>
                                              <AlertDialogTitle>{t("common.delete")}</AlertDialogTitle>
                                              <AlertDialogDescription>{iso.name}</AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                              <AlertDialogCancel className="border-slate-200">{t("common.cancel")}</AlertDialogCancel>
                                              <AlertDialogAction onClick={() => handleDeleteIsotope(iso.id, el.id)} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-xs text-slate-400 px-4 py-3 pl-12">{t("isotopes.noIsotopes")}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}

                {/* ====== SECURITY SECTION ====== */}
                {activeSection === "security" && (
                  <motion.div key="security" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                    <KeyRotationBanner />
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-slate-900">{t("settings.security")}</h2>
                      <p className="text-sm text-slate-500">{t("settings.securityDesc")}</p>
                    </div>

                    <div className="space-y-6">
                      {/* Maintenance mode. First in the section because it is the
                          only switch here that is meant to be used routinely,
                          and because leaving it on is the failure it has in
                          practice - so it should be the first thing seen. */}
                      <div
                        className={`bg-white border rounded-lg shadow-sm overflow-hidden ${maintenanceOn ? "border-amber-300" : "border-slate-200"}`}
                        data-testid="maintenance-section"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${maintenanceOn ? "bg-amber-100" : "bg-slate-100"}`}>
                              <Wrench className={`w-4 h-4 ${maintenanceOn ? "text-amber-600" : "text-slate-500"}`} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-slate-900">Modalità manutenzione</h3>
                              <p className="text-xs text-slate-500">Sospende le modifiche per gli utenti non amministratori</p>
                            </div>
                          </div>
                          <span
                            data-testid="maintenance-status-badge"
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border shrink-0 ${maintenanceOn ? PANEL_TONES.warn : PANEL_TONES.good}`}
                          >
                            {maintenanceOn ? <AlertCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            {maintenanceOn ? "Attiva" : "Non attiva"}
                          </span>
                        </div>

                        <div className="p-6 space-y-4">
                          <p className="text-sm text-slate-600 leading-relaxed">
                            Con la modalità attiva, gli utenti non amministratori possono consultare
                            progetti, file e planning ma non possono caricare, creare o modificare
                            nulla. Gli amministratori non subiscono limitazioni e l'accesso resta
                            possibile per tutti. Un avviso compare in cima a ogni pagina.
                          </p>

                          {maintenanceOn ? (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                              <p>
                                Attiva{maintenance?.by ? ` da ${maintenance.by}` : ""}
                                {maintenance?.since ? ` dal ${formatDate(maintenance.since)}` : ""}.
                              </p>
                              {maintenance?.reason && <p className="mt-1">{maintenance.reason}</p>}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Label htmlFor="maintenanceReason">Motivo (facoltativo)</Label>
                              <Input
                                id="maintenanceReason"
                                data-testid="maintenance-reason-input"
                                value={maintenanceReason}
                                onChange={(e) => setMaintenanceReason(e.target.value)}
                                placeholder="Compare nell'avviso mostrato agli utenti"
                                maxLength={200}
                                className="border-slate-200"
                              />
                            </div>
                          )}

                          <div className="flex justify-end">
                            <Button
                              onClick={handleToggleMaintenance}
                              disabled={togglingMaintenance}
                              data-testid="maintenance-toggle-btn"
                              className={maintenanceOn
                                ? "bg-emerald-600 hover:bg-emerald-500 text-white w-full sm:w-auto"
                                : "bg-amber-500 hover:bg-amber-400 text-amber-950 w-full sm:w-auto"}
                            >
                              {togglingMaintenance ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wrench className="w-4 h-4 mr-2" />}
                              {maintenanceOn ? "Disattiva la manutenzione" : "Attiva la manutenzione"}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Database Encryption */}
                      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                              <Lock className="w-4 h-4 text-indigo-600" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-slate-900">{t("security.dbEncryption")}</h3>
                              <p className="text-xs text-slate-500">{t("security.dbEncryptionDesc")}</p>
                            </div>
                          </div>
                          <span
                            data-testid="encryption-status-badge"
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border shrink-0 ${PANEL_TONES[dbKeyPanel.tone]}`}
                          >
                            {dbKeyPanel.tone === "good" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                            {dbKeyPanel.badge}
                          </span>
                        </div>

                        <div className="p-6 space-y-4">
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                              <p className="text-sm text-amber-800 font-medium">{t("security.keyWarning")}</p>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <p className="text-sm text-slate-700 min-w-0 flex-1">{dbKeyPanel.message}</p>
                            <Link
                              to="/settings/db-key"
                              data-testid="db-key-rotation-link"
                              className="inline-flex items-center justify-center gap-1.5 shrink-0 px-4 py-2 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white text-sm font-medium transition-colors"
                            >
                              {dbKeyPanel.cta}
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                      </div>

                      {/* File Storage Encryption */}
                      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                              <Shield className="w-4 h-4 text-indigo-600" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-slate-900">{t("security.fileEncryption")}</h3>
                              <p className="text-xs text-slate-500">{t("security.fileEncryptionDesc")}</p>
                            </div>
                          </div>
                          {fileKeyPanel && (
                            <span
                              data-testid="file-enc-status-badge"
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border shrink-0 ${PANEL_TONES[fileKeyPanel.tone]}`}
                            >
                              {fileKeyPanel.tone === "good" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                              {fileKeyPanel.badge}
                            </span>
                          )}
                        </div>

                        <div className="p-6">
                          {!fileKeyPanel ? (
                            <p className="text-sm text-slate-500">{t("r2.notConfigured")}</p>
                          ) : (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-slate-700">{fileKeyPanel.message}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Copia in lettura: <code className="px-1 bg-slate-100 rounded">{fileKeyStatus.active?.label}</code>
                                  {" "}nel bucket <code className="px-1 bg-slate-100 rounded">{fileKeyStatus.bucket}</code>
                                </p>
                              </div>
                              <Link
                                to="/settings/file-key"
                                data-testid="file-key-rotation-link"
                                className="inline-flex items-center justify-center gap-1.5 shrink-0 px-4 py-2 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white text-sm font-medium transition-colors"
                              >
                                {fileKeyPanel.cta}
                                <ArrowRight className="w-4 h-4" />
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ====== STORAGE (R2) SECTION ====== */}
                {activeSection === "storage" && (
                  <motion.div key="storage" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                    <div className="space-y-6">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="text-lg font-semibold text-slate-900">{t("r2.title")}</h2>
                          <p className="text-sm text-slate-500 mt-1">{t("r2.description")}</p>
                        </div>
                        <span data-testid="r2-status-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${r2Configured ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                          {r2Configured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                          {r2Configured ? t("r2.configured") : t("r2.notConfigured")}
                        </span>
                      </div>

                      {/* The credential fields carry no placeholder: theirs only
                          repeated the label directly above them. */}
                      <form onSubmit={handleSaveR2} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                        <div className="grid gap-5 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label>{t("r2.accessKeyId")}</Label>
                            <Input data-testid="r2-access-key" value={r2AccessKeyId} onChange={(e) => setR2AccessKeyId(e.target.value)} required className="border-slate-200" />
                          </div>
                          <div className="space-y-2">
                            <Label>{t("r2.secretAccessKey")}</Label>
                            <Input data-testid="r2-secret-key" type="password" value={r2SecretAccessKey} onChange={(e) => setR2SecretAccessKey(e.target.value)} required className="border-slate-200" />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label>{t("r2.endpointUrl")}</Label>
                            <Input data-testid="r2-endpoint" value={r2EndpointUrl} onChange={(e) => setR2EndpointUrl(e.target.value)} placeholder={t("r2.endpointPlaceholder")} required className="border-slate-200" />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label>{t("r2.bucketName")}</Label>
                            <Input data-testid="r2-bucket" value={r2BucketName} onChange={(e) => setR2BucketName(e.target.value)} placeholder={t("r2.bucketPlaceholder")} required className="border-slate-200" />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t border-slate-100">
                          <Button type="button" variant="outline" data-testid="r2-test-btn" onClick={handleTestR2} disabled={testingR2} className="border-slate-200 w-full sm:w-auto">
                            {testingR2 ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : r2TestResult?.connected ? <Wifi className="w-4 h-4 mr-2 text-emerald-600" /> : <WifiOff className="w-4 h-4 mr-2" />}
                            {testingR2 ? t("r2.testing") : t("r2.testConnection")}
                          </Button>
                          <Button type="submit" data-testid="r2-save-btn" disabled={savingR2} className="bg-indigo-700 hover:bg-indigo-600 text-white w-full sm:w-auto">
                            {savingR2 ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <HardDrive className="w-4 h-4 mr-2" />}
                            {savingR2 ? t("r2.saving") : t("r2.save")}
                          </Button>
                        </div>
                      </form>

                      {/* Test Connection Result */}
                      {r2TestResult && (
                        <div data-testid="r2-test-result" className={`flex items-start gap-3 p-4 rounded-lg border ${r2TestResult.connected ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                          {r2TestResult.connected ? <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" /> : <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />}
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{r2TestResult.connected ? t("r2.testSuccess") : t("r2.testFailed")}</p>
                            <p className="text-sm mt-0.5 opacity-80 break-words">{r2TestResult.connected ? r2TestResult.message : r2TestResult.error}</p>
                          </div>
                        </div>
                      )}

                      {/* Backup R2 Configuration */}
                      <div className="mt-8 pt-6 border-t border-slate-200">
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                          <div className="min-w-0">
                            <h3 className="text-base font-semibold text-slate-900">{t("backupR2.title")}</h3>
                            <p className="text-sm text-slate-500 mt-1">{t("backupR2.description")}</p>
                          </div>
                          <span data-testid="backup-r2-status-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${bkR2Configured ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                            {bkR2Configured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                            {bkR2Configured ? t("r2.configured") : t("r2.notConfigured")}
                          </span>
                        </div>

                        <form onSubmit={handleSaveBkR2} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                          <div className="grid gap-5 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label>{t("backupR2.accessKeyId")}</Label>
                              <Input data-testid="backup-r2-access-key" value={bkR2AccessKeyId} onChange={(e) => setBkR2AccessKeyId(e.target.value)} required className="border-slate-200" />
                            </div>
                            <div className="space-y-2">
                              <Label>{t("backupR2.secretAccessKey")}</Label>
                              <Input data-testid="backup-r2-secret-key" type="password" value={bkR2SecretAccessKey} onChange={(e) => setBkR2SecretAccessKey(e.target.value)} required className="border-slate-200" />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                              <Label>{t("backupR2.endpointUrl")}</Label>
                              <Input data-testid="backup-r2-endpoint" value={bkR2EndpointUrl} onChange={(e) => setBkR2EndpointUrl(e.target.value)} placeholder={t("r2.endpointPlaceholder")} required className="border-slate-200" />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                              <Label>{t("backupR2.bucketName")}</Label>
                              <Input data-testid="backup-r2-bucket" value={bkR2BucketName} onChange={(e) => setBkR2BucketName(e.target.value)} placeholder={t("backupR2.bucketPlaceholder")} required className="border-slate-200" />
                            </div>
                          </div>
                          <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                            <Button type="submit" data-testid="backup-r2-save-btn" disabled={savingBkR2} className="bg-indigo-700 hover:bg-indigo-600 text-white w-full sm:w-auto">
                              {savingBkR2 ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <HardDrive className="w-4 h-4 mr-2" />}
                              {savingBkR2 ? t("r2.saving") : t("r2.save")}
                            </Button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </motion.div>
                )}

              </div>
            </div>
          )}
        </motion.div>
      </main>

      {/* ========== DIALOGS (shared across sections) ========== */}

      {/* User Edit Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 max-h-[90vh] flex flex-col">
          <form onSubmit={handleSaveUser} className="flex flex-col min-h-0 flex-1">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 bg-white sticky top-0 z-10 rounded-t-lg">
              <DialogTitle className="text-xl font-bold">{t("users.edit.title")}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="userDisplayName">{t("users.edit.displayName")}</Label>
                <Input id="userDisplayName" data-testid="edit-user-name-input" value={userDisplayName} onChange={(e) => setUserDisplayName(e.target.value)} placeholder={t("users.edit.displayName")} className="border-slate-200" />
              </div>
              <div className="space-y-2">
                <ColorPicker label={t("users.edit.color")} value={userColor} onChange={setUserColor} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="userRole">{t("users.edit.role")}</Label>
                <Select value={userRole} onValueChange={setUserRole}>
                  <SelectTrigger data-testid="edit-user-role-select" className="border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{t("users.admin")}</SelectItem>
                    <SelectItem value="user">{t("users.userRole")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <PasswordInput
                  password={userPassword}
                  setPassword={setUserPassword}
                  confirmPassword={userConfirmPassword}
                  setConfirmPassword={setUserConfirmPassword}
                  labelKey="users.edit.newPassword"
                  confirmLabelKey="pwd.confirmPassword"
                />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-white sticky bottom-0 z-10 rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)} className="border-slate-200">{t("users.edit.cancel")}</Button>
              <Button type="submit" data-testid="save-user-btn" disabled={savingUser} className="bg-indigo-700 hover:bg-indigo-600 text-white">
                {savingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : t("users.edit.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 max-h-[90vh] flex flex-col">
          <form onSubmit={handleCreateUser} className="flex flex-col min-h-0 flex-1">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 bg-white sticky top-0 z-10 rounded-t-lg">
              <DialogTitle className="text-xl font-bold">{t("users.create.title")}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newUserEmail">{t("users.create.email")}</Label>
                <Input id="newUserEmail" data-testid="new-user-email-input" type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder={t("users.create.emailPlaceholder")} required className="border-slate-200" />
              </div>
              <div className="space-y-2">
                <PasswordInput
                  password={newUserPassword}
                  setPassword={setNewUserPassword}
                  confirmPassword={newUserConfirmPassword}
                  setConfirmPassword={setNewUserConfirmPassword}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newUserDisplayName">{t("users.create.displayName")}</Label>
                <Input id="newUserDisplayName" data-testid="new-user-display-name-input" value={newUserDisplayName} onChange={(e) => setNewUserDisplayName(e.target.value)} placeholder={t("users.create.displayNamePlaceholder")} required className="border-slate-200" />
              </div>
              <div className="space-y-2">
                <ColorPicker label={t("users.edit.color")} value={newUserColor} onChange={setNewUserColor} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newUserRole">{t("users.create.role")}</Label>
                <Select value={newUserRole} onValueChange={setNewUserRole}>
                  <SelectTrigger data-testid="new-user-role-select" className="border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{t("users.admin")}</SelectItem>
                    <SelectItem value="user">{t("users.userRole")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-white sticky bottom-0 z-10 rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => setCreateUserDialogOpen(false)} className="border-slate-200">{t("users.create.cancel")}</Button>
              <Button type="submit" data-testid="create-user-btn" disabled={creatingUser} className="bg-indigo-700 hover:bg-indigo-600 text-white">
                {creatingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : t("users.create.submit")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Element Dialog */}
      <Dialog open={elementDialogOpen} onOpenChange={setElementDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 max-h-[90vh] flex flex-col">
          <form onSubmit={handleSaveElement} className="flex flex-col min-h-0 flex-1">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 bg-white sticky top-0 z-10 rounded-t-lg">
              <DialogTitle className="text-xl font-bold">{editingElement ? t("isotopes.editElement") : t("isotopes.newElement")}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label>{t("isotopes.elementName")}</Label>
                <Input data-testid="element-name-input" value={elementName} onChange={(e) => setElementName(e.target.value)} placeholder={t("isotopes.elementPlaceholder")} required className="border-slate-200" />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-white sticky bottom-0 z-10 rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => setElementDialogOpen(false)} className="border-slate-200">{t("common.cancel")}</Button>
              <Button type="submit" disabled={savingElement} data-testid="save-element-btn" className="bg-indigo-700 hover:bg-indigo-600 text-white">
                {savingElement ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Isotope Dialog */}
      <Dialog open={isotopeDialogOpen} onOpenChange={setIsotopeDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 max-h-[90vh] flex flex-col">
          <form onSubmit={handleSaveIsotope} className="flex flex-col min-h-0 flex-1">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 bg-white sticky top-0 z-10 rounded-t-lg">
              <DialogTitle className="text-xl font-bold">{editingIsotope ? t("isotopes.editIsotope") : t("isotopes.newIsotope")}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label>{t("isotopes.isotopeName")}</Label>
                <Input data-testid="isotope-name-input" value={isotopeName} onChange={(e) => setIsotopeName(e.target.value)} placeholder={t("isotopes.isotopePlaceholder")} required className="border-slate-200" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("isotopes.halfLifeValue")}</Label>
                  <Input data-testid="isotope-halflife-value" type="number" min="0" step="any" value={isotopeHalfLifeValue} onChange={(e) => setIsotopeHalfLifeValue(e.target.value)} placeholder="67.71" required className="border-slate-200" />
                </div>
                <div className="space-y-2">
                  <Label>{t("isotopes.halfLifeUnit")}</Label>
                  <Select value={isotopeHalfLifeUnit} onValueChange={setIsotopeHalfLifeUnit}>
                    <SelectTrigger data-testid="isotope-halflife-unit" className="border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {HALF_LIFE_UNITS.map((u) => (
                        <SelectItem key={u} value={u}>{t(`isotopes.${u}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-white sticky bottom-0 z-10 rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => setIsotopeDialogOpen(false)} className="border-slate-200">{t("common.cancel")}</Button>
              <Button type="submit" disabled={savingIsotope} data-testid="save-isotope-btn" className="bg-indigo-700 hover:bg-indigo-600 text-white">
                {savingIsotope ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
